import {
  Badge,
  Box,
  Button,
  Card,
  Code,
  Flex,
  Heading,
  HStack,
  Input,
  NativeSelect,
  Spinner,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react'
import { ChevronLeftIcon, ChevronRightIcon, SearchIcon, XIcon as ClearIcon } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { RefreshCwIcon, RotateCcwIcon, ScrollTextIcon, Trash2Icon, XIcon } from 'lucide-react'
import { useState } from 'react'
import { k8sApi, type Pod } from '~/lib/api'
import { toaster } from '~/toaster'

interface PodsTabProps {
  namespace: string
  isAdmin: boolean
}

function StatusBadge({ status }: { status: string }) {
  const colorScheme =
    {
      Running: 'green',
      Pending: 'yellow',
      Succeeded: 'blue',
      Failed: 'red',
      Unknown: 'gray',
    }[status] || 'gray'

  return (
    <Badge colorPalette={colorScheme} variant="subtle">
      {status}
    </Badge>
  )
}

function PodLogsModal({ pod, onClose }: { pod: Pod; onClose: () => void }) {
  const [containerName, setContainerName] = useState(pod.containers[0]?.name || '')
  const tailLines = 500

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['pod-logs', pod.namespace, pod.name, containerName, tailLines],
    queryFn: () =>
      k8sApi.podLogs(pod.namespace, pod.name, {
        container: containerName || undefined,
        tailLines,
      }),
    enabled: !!pod,
  })

  return (
    <Box position="fixed" inset={0} bg="blackAlpha.600" zIndex={1000} onClick={onClose}>
      <Box
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        bg="bg.panel"
        borderRadius="lg"
        w="90vw"
        maxW="1200px"
        h="80vh"
        overflow="hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex
          justify="space-between"
          align="center"
          p={4}
          borderBottomWidth="1px"
          borderColor="border"
        >
          <VStack align="flex-start" gap={0}>
            <Heading size="md">Pod Logs</Heading>
            <Text fontSize="sm" color="content.secondary">
              {pod.namespace}/{pod.name}
            </Text>
          </VStack>
          <HStack gap={4}>
            {pod.containers.length > 1 && (
              <NativeSelect.Root size="sm" w="200px">
                <NativeSelect.Field
                  aria-label="Select container"
                  value={containerName}
                  onChange={(e) => setContainerName(e.target.value)}
                >
                  {pod.containers.map((c) => (
                    <option key={c.name} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </NativeSelect.Field>
              </NativeSelect.Root>
            )}
            <Button size="sm" variant="outline" onClick={() => refetch()}>
              <RefreshCwIcon size={16} />
              Refresh
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onClose}
              aria-label="Close pod logs"
              minW="44px"
              minH="44px"
            >
              <XIcon size={16} />
            </Button>
          </HStack>
        </Flex>
        <Box h="calc(100% - 80px)" overflow="auto" p={4} bg="gray.900">
          {isLoading ? (
            <Flex h="100%" align="center" justify="center">
              <Spinner color="white" />
            </Flex>
          ) : (
            <Code
              display="block"
              whiteSpace="pre-wrap"
              wordBreak="break-all"
              fontSize="xs"
              color="green.300"
              bg="transparent"
              p={0}
            >
              {data?.logs || 'No logs available'}
            </Code>
          )}
        </Box>
      </Box>
    </Box>
  )
}

const PAGE_SIZE = 10

export function PodsTab({ namespace, isAdmin }: PodsTabProps) {
  const queryClient = useQueryClient()
  const [logsModalPod, setLogsModalPod] = useState<Pod | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')

  // Fetch pods
  const {
    data: podsData,
    isLoading: podsLoading,
    refetch: refetchPods,
  } = useQuery({
    queryKey: ['pods', namespace],
    queryFn: () => k8sApi.pods(namespace || undefined),
    retry: false,
  })

  // Restart pod mutation
  const restartMutation = useMutation({
    mutationFn: ({ namespace, name }: { namespace: string; name: string }) =>
      k8sApi.restartPod(namespace, name),
    onSuccess: (data) => {
      toaster.create({
        title: 'Pod restart initiated',
        description: data.message,
        type: 'success',
      })
      queryClient.invalidateQueries({ queryKey: ['pods'] })
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to restart pod',
        description: error.message,
        type: 'error',
      })
    },
  })

  // Kill pod mutation
  const killMutation = useMutation({
    mutationFn: ({ namespace, name }: { namespace: string; name: string }) =>
      k8sApi.killPod(namespace, name),
    onSuccess: (data) => {
      toaster.create({
        title: 'Pod killed',
        description: data.message,
        type: 'success',
      })
      queryClient.invalidateQueries({ queryKey: ['pods'] })
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to kill pod',
        description: error.message,
        type: 'error',
      })
    },
  })

  const handleKillPod = (namespace: string, name: string) => {
    if (
      window.confirm(
        `Are you sure you want to kill pod "${name}"?\n\nThis will immediately terminate the pod with no grace period.`,
      )
    ) {
      killMutation.mutate({ namespace, name })
    }
  }

  const allPods = podsData?.pods || []

  // Filter pods based on search query
  const filteredPods = allPods.filter((pod) => {
    if (!searchQuery.trim()) return true
    const query = searchQuery.toLowerCase()
    return (
      pod.name.toLowerCase().includes(query) ||
      pod.namespace.toLowerCase().includes(query) ||
      pod.status.toLowerCase().includes(query) ||
      pod.nodeName?.toLowerCase().includes(query) ||
      pod.podIP?.toLowerCase().includes(query)
    )
  })

  // Pagination (on filtered results)
  const totalPages = Math.ceil(filteredPods.length / PAGE_SIZE)
  const startIndex = (currentPage - 1) * PAGE_SIZE
  const endIndex = startIndex + PAGE_SIZE
  const pods = filteredPods.slice(startIndex, endIndex)

  // Reset to page 1 when search changes
  const handleSearchChange = (query: string) => {
    setSearchQuery(query)
    setCurrentPage(1)
  }

  const clearSearch = () => {
    setSearchQuery('')
    setCurrentPage(1)
  }

  return (
    <VStack align="stretch" gap={4}>
      <Flex justify="space-between" align="center">
        <Box position="relative" w="300px">
          <HStack
            position="absolute"
            left={3}
            top="50%"
            transform="translateY(-50%)"
            zIndex={1}
            pointerEvents="none"
          >
            <SearchIcon size={16} color="gray" />
          </HStack>
          <Input
            size="sm"
            pl={10}
            pr={searchQuery ? 10 : 3}
            placeholder="Search pods by name, namespace, status..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {searchQuery && (
            <Button
              size="xs"
              variant="ghost"
              position="absolute"
              right={1}
              top="50%"
              transform="translateY(-50%)"
              onClick={clearSearch}
              aria-label="Clear search"
              minW="auto"
              h="auto"
              p={1}
            >
              <ClearIcon size={14} />
            </Button>
          )}
        </Box>
        <Button size="sm" onClick={() => refetchPods()}>
          <RefreshCwIcon size={16} />
          Refresh
        </Button>
      </Flex>

      <Card.Root>
        <Table.Root>
          <Table.Header>
            <Table.Row bg="bg.subtle">
              <Table.ColumnHeader>Name</Table.ColumnHeader>
              <Table.ColumnHeader>Namespace</Table.ColumnHeader>
              <Table.ColumnHeader>Status</Table.ColumnHeader>
              <Table.ColumnHeader>Ready</Table.ColumnHeader>
              <Table.ColumnHeader>Restarts</Table.ColumnHeader>
              <Table.ColumnHeader>Age</Table.ColumnHeader>
              <Table.ColumnHeader>Actions</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {podsLoading ? (
              <Table.Row>
                <Table.Cell colSpan={7}>
                  <Flex justify="center" py={8}>
                    <Spinner />
                  </Flex>
                </Table.Cell>
              </Table.Row>
            ) : pods.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={7}>
                  <VStack py={8} gap={2}>
                    <Text textAlign="center" color="content.secondary">
                      {searchQuery ? `No pods matching "${searchQuery}"` : 'No pods found'}
                    </Text>
                    {searchQuery && (
                      <Button size="sm" variant="ghost" onClick={clearSearch}>
                        Clear search
                      </Button>
                    )}
                  </VStack>
                </Table.Cell>
              </Table.Row>
            ) : (
              pods.map((pod) => (
                <Table.Row key={`${pod.namespace}/${pod.name}`}>
                  <Table.Cell>
                    <Text fontWeight="medium">{pod.name}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge variant="outline">{pod.namespace}</Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <StatusBadge status={pod.status} />
                  </Table.Cell>
                  <Table.Cell>{pod.ready}</Table.Cell>
                  <Table.Cell>{pod.restarts}</Table.Cell>
                  <Table.Cell>{pod.age}</Table.Cell>
                  <Table.Cell>
                    <HStack gap={2}>
                      <Button size="xs" variant="ghost" onClick={() => setLogsModalPod(pod)}>
                        <ScrollTextIcon size={14} />
                        Logs
                      </Button>
                      {isAdmin && (
                        <>
                          <Button
                            size="xs"
                            variant="ghost"
                            colorPalette="orange"
                            loading={restartMutation.isPending}
                            onClick={() =>
                              restartMutation.mutate({
                                namespace: pod.namespace,
                                name: pod.name,
                              })
                            }
                          >
                            <RotateCcwIcon size={14} />
                            Restart
                          </Button>
                          <Button
                            size="xs"
                            variant="ghost"
                            colorPalette="red"
                            loading={killMutation.isPending}
                            onClick={() => handleKillPod(pod.namespace, pod.name)}
                          >
                            <Trash2Icon size={14} />
                            Kill
                          </Button>
                        </>
                      )}
                    </HStack>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table.Root>
      </Card.Root>

      {/* Pagination */}
      {totalPages > 1 && (
        <Flex justify="space-between" align="center" pt={2}>
          <Text fontSize="sm" color="content.secondary">
            Showing {startIndex + 1}-{Math.min(endIndex, filteredPods.length)} of{' '}
            {filteredPods.length} pods
            {searchQuery && ` (filtered from ${allPods.length})`}
          </Text>
          <HStack gap={2}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeftIcon size={16} />
              Previous
            </Button>
            <HStack gap={1}>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <Button
                  key={page}
                  size="sm"
                  variant={page === currentPage ? 'solid' : 'outline'}
                  colorPalette={page === currentPage ? 'blue' : undefined}
                  onClick={() => setCurrentPage(page)}
                  aria-label={`Page ${page}`}
                  aria-current={page === currentPage ? 'page' : undefined}
                  minW="44px"
                  minH="44px"
                >
                  {page}
                </Button>
              ))}
            </HStack>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
              <ChevronRightIcon size={16} />
            </Button>
          </HStack>
        </Flex>
      )}

      {logsModalPod && <PodLogsModal pod={logsModalPod} onClose={() => setLogsModalPod(null)} />}
    </VStack>
  )
}
